import { SB } from '@play-co/replicant';
import { getLocaleConfig } from 'src/replicant/creatives/text';
import {
  createFlexContainer,
  createFlexMessageTemplate,
  createOpenGameAction,
} from './utils';
import { getPetAssetKey, speciesChatbot } from './species';
import { duration } from 'src/replicant/utils/duration';

/**
 * Assets for duo messages
 */
export const duoAssets = {
  'duo/background/invite': 'private/chatbot/duo/invite_background.png',
  'duo/background/response': 'private/chatbot/duo/response_background.png',
  'duo/please/red': 'private/chatbot/duo/please_red.png',
  'duo/please/gold': 'private/chatbot/duo/please_gold.png',
  'species/giraffe/invite': 'private/chatbot/species/giraffe/invite.png',
  'species/giraffe/invite/ultimate':
    'private/chatbot/species/giraffe/invite_ultimate.png',
  'species/whale/invite': 'private/chatbot/species/whale/invite.png',
  'species/whale/invite/ultimate':
    'private/chatbot/species/whale/invite_ultimate.png',
};

/**
 * Create duo invite message
 *
 * https://developers.line.biz/en/docs/messaging-api/flex-message-elements/
 * https://developers.line.biz/en/docs/messaging-api/using-flex-messages/
 *
 * Tools for generation of messages in flex format:
 * https://developers.line.biz/flex-simulator/
 */
export const duoInviteMessage = createFlexMessageTemplate(
  SB.object({
    locale: SB.tuple(['en', 'ja']),
    expiration: SB.number(),
    item: SB.string(),
    inviter: SB.object({
      name: SB.string(),
      photo: SB.string(),
      pet: SB.tuple(speciesChatbot),
      petName: SB.string(),
    }),
  }),
  (data, payload, api) => {
    const text = getLocaleConfig(data.locale).duo.invite.text.replace(
      '{name}',
      data.inviter.name,
    );

    const hours = Math.floor(data.expiration / duration({ hours: 1 }));
    const minutes = Math.floor(
      (data.expiration % duration({ hours: 1 })) / duration({ minutes: 1 }),
    );

    const cooldownText = getLocaleConfig(data.locale)
      .duo.expiration.text.replace('{hh}', hours.toString())
      .replace('{mm}', minutes.toString());

    const isUltimateItem = data.item === 'i_ultimateVowsCoffee';

    const friendIcon = api.getAssetPath(
      `species/${data.inviter.pet}/invite${isUltimateItem ? '/ultimate' : ''}`,
    );

    const template = createFlexContainer(text, {
      type: 'bubble',
      size: 'giga',
      action: createOpenGameAction(text, payload),
      body: {
        type: 'box',
        layout: 'vertical',
        paddingAll: '0px',
        contents: [
          {
            type: 'image',
            url: api.getAssetPath('duo/background/invite'),
            size: 'full',
            gravity: 'center',
            aspectRatio: '1.3:1',
            animated: false,
            aspectMode: 'cover',
          },
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'image',
                url: data.inviter.photo,
                aspectRatio: '1:1',
                aspectMode: 'cover',
                gravity: 'center',
                size: 'full',
              },
            ],
            position: 'absolute',
            offsetEnd: '5.1%',
            height: '30%',
            width: '20.1%',
            offsetTop: '5.8%',
          },
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'image',
                url: friendIcon,
                size: 'full',
              },
            ],
            position: 'absolute',
            width: '49%',
            height: '80%',
            offsetBottom: '-7.5%',
            offsetStart: '-3.5%',
          },
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'image',
                url: api.getAssetPath(
                  `duo/please/${isUltimateItem ? 'gold' : 'red'}`,
                ),
                size: 'full',
              },
            ],
            position: 'absolute',
            width: '50%',
            offsetStart: '33%',
            offsetBottom: '18%',
          },
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'text',
                text,
                color: '#ffffff',
                wrap: true,
                align: 'end',
                size: '19px',
                weight: 'bold',
              },
            ],
            position: 'absolute',
            width: '70%',
            height: '20%',
            offsetTop: '5%',
            offsetStart: '3%',
          },
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'text',
                text: cooldownText,
                color: '#ffffff',
                wrap: true,
                align: 'center',
                size: '15px',
                weight: 'bold',
              },
            ],
            position: 'absolute',
            width: '100%',
            height: '20px',
            offsetBottom: '2%',
          },
        ],
      },
    });

    // Add sender to message
    template['sender'] = {
      name: data.inviter.petName,
      iconUrl: api.getAssetPath(getPetAssetKey(data.inviter.pet)),
    };

    return template;
  },
);

/**
 * Create duo reminder message
 *
 * https://developers.line.biz/en/docs/messaging-api/flex-message-elements/
 * https://developers.line.biz/en/docs/messaging-api/using-flex-messages/
 *
 * Tools for generation of messages in flex format:
 * https://developers.line.biz/flex-simulator/
 */
export const duoReminderMessage = createFlexMessageTemplate(
  SB.object({
    locale: SB.tuple(['en', 'ja']),
    expiration: SB.number(),
    item: SB.string(),
    inviter: SB.object({
      name: SB.string(),
      photo: SB.string(),
      pet: SB.tuple(speciesChatbot),
      petName: SB.string(),
    }),
  }),
  (data, payload, api) => {
    const text = getLocaleConfig(data.locale).duo.reminder.text.replace(
      '{name}',
      data.inviter.name,
    );

    const hours = Math.floor(data.expiration / duration({ hours: 1 }));
    const minutes = Math.floor(
      (data.expiration % duration({ hours: 1 })) / duration({ minutes: 1 }),
    );

    const cooldownText = getLocaleConfig(data.locale)
      .duo.expiration.text.replace('{hh}', hours.toString())
      .replace('{mm}', minutes.toString());

    const isUltimateItem = data.item === 'i_ultimateVowsCoffee';

    const friendIcon = api.getAssetPath(
      `species/${data.inviter.pet}/invite${isUltimateItem ? '/ultimate' : ''}`,
    );

    const template = createFlexContainer(text, {
      type: 'bubble',
      size: 'giga',
      action: createOpenGameAction(text, payload),
      body: {
        type: 'box',
        layout: 'vertical',
        paddingAll: '0px',
        contents: [
          {
            type: 'image',
            url: api.getAssetPath('duo/background/invite'),
            size: 'full',
            gravity: 'center',
            aspectRatio: '1.3:1',
            animated: false,
            aspectMode: 'cover',
          },
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'image',
                url: data.inviter.photo,
                aspectRatio: '1:1',
                aspectMode: 'cover',
                gravity: 'center',
                size: 'full',
              },
            ],
            position: 'absolute',
            offsetEnd: '5.1%',
            height: '30%',
            width: '20.1%',
            offsetTop: '5.8%',
          },
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'image',
                url: friendIcon,
                size: 'full',
              },
            ],
            position: 'absolute',
            width: '49%',
            height: '80%',
            offsetBottom: '-7.5%',
            offsetStart: '-3.5%',
          },
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'image',
                url: api.getAssetPath(
                  `duo/please/${isUltimateItem ? 'gold' : 'red'}`,
                ),
                size: 'full',
              },
            ],
            position: 'absolute',
            width: '50%',
            offsetStart: '33%',
            offsetBottom: '18%',
          },
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'text',
                text,
                color: '#ffffff',
                wrap: true,
                align: 'end',
                size: '19px',
                weight: 'bold',
              },
            ],
            position: 'absolute',
            width: '70%',
            height: '20%',
            offsetTop: '5%',
            offsetStart: '3%',
          },
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'text',
                text: cooldownText,
                color: '#ffffff',
                wrap: true,
                align: 'center',
                size: '15px',
                weight: 'bold',
              },
            ],
            position: 'absolute',
            width: '100%',
            height: '20px',
            offsetBottom: '2%',
          },
        ],
      },
    });

    // Add sender to message
    template['sender'] = {
      name: data.inviter.petName,
      iconUrl: api.getAssetPath(getPetAssetKey(data.inviter.pet)),
    };

    return template;
  },
);

/**
 * Create duo response message
 *
 * https://developers.line.biz/en/docs/messaging-api/flex-message-elements/
 * https://developers.line.biz/en/docs/messaging-api/using-flex-messages/
 *
 * Tools for generation of messages in flex format:
 * https://developers.line.biz/flex-simulator/
 */
export const duoResponseMessage = createFlexMessageTemplate(
  SB.object({
    locale: SB.tuple(['en', 'ja']),
    invitee: SB.object({
      name: SB.string(),
      photo: SB.string(),
      pet: SB.tuple(speciesChatbot),
      petName: SB.string(),
    }),
  }),
  (data, payload, api) => {
    const text = getLocaleConfig(data.locale).duo.response.text.replace(
      '{name}',
      data.invitee.name,
    );

    const speechBubble = getLocaleConfig(data.locale).duo.bubble.text;

    const friendAssetKey = getPetAssetKey(data.invitee.pet, 'happy');
    const friendIcon = api.getAssetPath(friendAssetKey);

    const template = createFlexContainer(text, {
      type: 'bubble',
      size: 'giga',
      action: createOpenGameAction(text, payload),
      body: {
        type: 'box',
        layout: 'vertical',
        paddingAll: '0px',
        contents: [
          {
            type: 'image',
            url: api.getAssetPath('duo/background/response'),
            size: 'full',
            gravity: 'center',
            aspectRatio: '1.3:1',
            animated: false,
            aspectMode: 'cover',
          },
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'image',
                url: data.invitee.photo,
                aspectRatio: '1:1',
                aspectMode: 'cover',
                gravity: 'center',
                size: 'full',
              },
            ],
            position: 'absolute',
            offsetEnd: '5.1%',
            height: '30%',
            width: '20.1%',
            offsetTop: '5.8%',
          },
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'image',
                url: friendIcon,
                size: 'full',
              },
            ],
            position: 'absolute',
            width: '40%',
            height: '70%',
            offsetBottom: '4.5%',
            offsetStart: '2%',
          },
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'text',
                text,
                color: '#ffffff',
                wrap: true,
                align: 'end',
                size: '21px',
                weight: 'bold',
              },
            ],
            position: 'absolute',
            width: '70%',
            height: '20%',
            offsetTop: '5%',
            offsetStart: '2%',
          },
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'text',
                text: speechBubble,
                color: '#8e818b',
                align: 'center',
                size: '19px',
                weight: 'bold',
              },
            ],
            position: 'absolute',
            width: '43%',
            height: '25px',
            offsetTop: '50%',
            offsetStart: '48%',
          },
        ],
      },
    });

    // Add sender to message
    template['sender'] = {
      name: data.invitee.petName,
      iconUrl: api.getAssetPath(getPetAssetKey(data.invitee.pet)),
    };

    return template;
  },
);
